import { Directive, EventEmitter, HostListener, Output } from '@angular/core';

/**
 * Reusable long-press directive.
 * Emits when the user holds down for 500ms (industry standard threshold).
 * Suppresses the subsequent click event after a successful long-press
 * to prevent both long-press AND click from firing.
 *
 * Usage: <button appLongPress (appLongPress)="onLongPress()">Hold me</button>
 */
@Directive({ selector: '[appLongPress]', standalone: true })
export class LongPressDirective {
  @Output() appLongPress = new EventEmitter<void>();

  private timeout: ReturnType<typeof setTimeout> | null = null;
  private triggered = false;

  @HostListener('mousedown', ['$event'])
  @HostListener('touchstart', ['$event'])
  onPress(_event: Event): void {
    // Clear any existing timeout before starting a new one (handles mouse+touch overlap)
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.triggered = false;
    this.timeout = setTimeout(() => {
      this.triggered = true;
      this.appLongPress.emit();
    }, 500);
  }

  @HostListener('mouseup')
  @HostListener('mouseleave')
  @HostListener('touchend')
  @HostListener('touchcancel')
  onRelease(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  // Prevent click from firing after long-press
  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (this.triggered) {
      event.preventDefault();
      event.stopPropagation();
      this.triggered = false;
    }
  }
}
